import { StatCard } from '@/components/ui/stat-card';
import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import { Bot, BarChart2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAIToolsMonthly, useExecutiveSummary } from '@/hooks/useSubscriptions';
import type { GlobalSubscriptionFilters } from '../../../../shared/types';
import { WinclapLoader } from '@/components/ui/winclap-loader';

const fmt = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const fmtShortMonth = (m: string) => {
  const [y, mo] = m.split('-');
  return new Date(+y, +mo - 1).toLocaleString('en-US', { month: 'short', year: '2-digit' });
};

const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#6366f1'];


export function AIToolsTab({ globalFilters = {} }: { globalFilters?: GlobalSubscriptionFilters }) {
  const { t } = useTranslation();
  const { data: aiRows = [], isLoading: loadingAI } = useAIToolsMonthly({
    dateFrom: globalFilters.dateFrom,
    dateTo: globalFilters.dateTo,
    vendor: globalFilters.vendor,
  });
  const { data: summary = [], isLoading: loadingSummary } = useExecutiveSummary();

  const allMonths = [...new Set(aiRows.map((r) => r.Month))].sort();
  const latestMonth = allMonths[allMonths.length - 1];
  const latestSummary = summary[summary.length - 1];

  const ytdAI = aiRows.reduce((s, r) => s + r.Total_Amount, 0);
  const currentMonthAI = aiRows.filter((r) => r.Month === latestMonth).reduce((s, r) => s + r.Total_Amount, 0);
  const avgMonthly = allMonths.length > 0 ? ytdAI / allMonths.length : 0;
  const allVendors = new Set(aiRows.map((r) => r.Normalized_Vendor));

  // Top vendors by total spend
  const vendorTotals = useMemo(() => {
    const map = new Map<string, { total: number; txns: number; users: number; cards: number; months: string[] }>();
    for (const r of aiRows) {
      const existing = map.get(r.Normalized_Vendor) ?? { total: 0, txns: 0, users: 0, cards: 0, months: [] };
      map.set(r.Normalized_Vendor, {
        total: existing.total + r.Total_Amount,
        txns: existing.txns + r.Transactions,
        users: Math.max(existing.users, r.Users),
        cards: Math.max(existing.cards, r.Cards),
        months: [...existing.months, r.Month],
      });
    }
    return [...map.entries()]
      .map(([vendor, data]) => ({ vendor, ...data }))
      .sort((a, b) => b.total - a.total);
  }, [aiRows]);

  // Top 8 vendors for stacked chart
  const top8Vendors = vendorTotals.slice(0, 8).map((v) => v.vendor);

  // Stacked area chart data
  const stackedData = allMonths.map((m) => {
    const entry: Record<string, any> = { month: fmtShortMonth(m) };
    let othersTotal = 0;
    for (const r of aiRows.filter((row) => row.Month === m)) {
      if (top8Vendors.includes(r.Normalized_Vendor)) {
        entry[r.Normalized_Vendor] = (entry[r.Normalized_Vendor] ?? 0) + r.Total_Amount;
      } else {
        othersTotal += r.Total_Amount;
      }
    }
    if (othersTotal > 0) entry['Others'] = othersTotal;
    return entry;
  });

  // AI share over time from exec summary
  const aiShareData = summary.map((r) => ({
    month: fmtShortMonth(r.Month),
    'AI Share %': +(r.AI_Share * 100).toFixed(1),
    'AI Vendors': r.AI_Vendors,
  }));

  // New vendor detection — first month of spend
  const vendorFirstMonth = new Map<string, string>();
  for (const r of aiRows) {
    const existing = vendorFirstMonth.get(r.Normalized_Vendor);
    if (!existing || r.Month < existing) vendorFirstMonth.set(r.Normalized_Vendor, r.Month);
  }
  const newVendorsThisMonth = [...vendorFirstMonth.entries()]
    .filter(([, firstMonth]) => firstMonth === latestMonth)
    .map(([vendor]) => vendor);

  // Concentration insight
  const top3Total = vendorTotals.slice(0, 3).reduce((s, v) => s + v.total, 0);
  const top3Share = ytdAI > 0 ? (top3Total / ytdAI * 100).toFixed(1) : '0';

  // Optimization candidates: high spend, low user breadth
  const avgSpend = ytdAI / (vendorTotals.length || 1);
  const optimizationCandidates = vendorTotals.filter(
    (v) => v.total > avgSpend * 0.5 && v.users <= 2
  ).slice(0, 8).map((v) => ({
    ...v,
    reason: v.users === 1 ? 'Single-user subscription' : 'Low user breadth vs cost',
  }));

  // MoM growth per vendor (latest vs previous month)
  const prevMonth = allMonths[allMonths.length - 2];
  const vendorGrowth = vendorTotals.map((v) => {
    const curAmount = aiRows.filter((r) => r.Month === latestMonth && r.Normalized_Vendor === v.vendor).reduce((s, r) => s + r.Total_Amount, 0);
    const prevAmount = aiRows.filter((r) => r.Month === prevMonth && r.Normalized_Vendor === v.vendor).reduce((s, r) => s + r.Total_Amount, 0);
    const growth = prevAmount > 0 ? ((curAmount - prevAmount) / prevAmount * 100) : null;
    return { vendor: v.vendor, total: v.total, growth };
  }).filter((v) => v.growth !== null).sort((a, b) => (b.growth ?? 0) - (a.growth ?? 0));

  const isLoading = loadingAI || loadingSummary;

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-4">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatCard size="sm" icon={Bot} label={t('subscriptions.ai_tools.kpi_ai_ytd')} value={fmt(ytdAI)} iconClassName="text-purple-500" />
        <StatCard size="sm"
          icon={Bot} label={t('subscriptions.ai_tools.kpi_current')} value={fmt(currentMonthAI)}
          trend={latestSummary?.AI_Tools_MoM_Growth ?? null} trendLabel={(v) => t('subscriptions.aitoolstab.v_mom', { v: (v >= 0 ? '+' : '') + v.toFixed(1) })} iconClassName="text-violet-500"
        />
        <StatCard size="sm" icon={BarChart2} label={t('subscriptions.ai_tools.kpi_avg')} value={fmt(avgMonthly)} iconClassName="text-indigo-500" />
        <StatCard size="sm" icon={Bot} label={t('subscriptions.ai_tools.kpi_vendors')} value={String(allVendors.size)} iconClassName="text-fuchsia-500" />
        <StatCard size="sm" icon={BarChart2} label={t('subscriptions.ai_tools.kpi_share')} value={latestSummary ? `${(latestSummary.AI_Share * 100).toFixed(1)}%` : '—'} iconClassName="text-emerald-500" />
      </div>

      {/* Insights */}
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-3">{t('subscriptions.charts.key_insights')}</h3>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="flex gap-2 text-sm">
            <span className="text-purple-500 mt-0.5">✦</span>
            <span><Trans i18nKey="subscriptions.aitoolstab.top3_represent" values={{ pct: top3Share, spread: t(Number(top3Share) > 80 ? 'subscriptions.aitoolstab.high_concentration' : 'subscriptions.aitoolstab.moderate_spread') }} components={{ b: <strong /> }} /></span>
          </div>
          {vendorGrowth.length > 0 && (
            <div className="flex gap-2 text-sm">
              <span className="text-purple-500 mt-0.5">✦</span>
              <span><Trans i18nKey="subscriptions.aitoolstab.fastest_growing" values={{ vendor: vendorGrowth[0].vendor, v: (vendorGrowth[0].growth! >= 0 ? '+' : '') + vendorGrowth[0].growth!.toFixed(1) }} components={{ b: <strong /> }} /></span>
            </div>
          )}
          {newVendorsThisMonth.length > 0 && (
            <div className="flex gap-2 text-sm">
              <span className="text-purple-500 mt-0.5">✦</span>
              <span><Trans i18nKey="subscriptions.aitoolstab.new_vendors" values={{ list: newVendorsThisMonth.join(', ') }} components={{ b: <strong /> }} /></span>
            </div>
          )}
          {vendorGrowth.length > 1 && vendorGrowth[vendorGrowth.length - 1].growth! < 0 && (
            <div className="flex gap-2 text-sm">
              <span className="text-purple-500 mt-0.5">✦</span>
              <span><Trans i18nKey="subscriptions.aitoolstab.decreasing_spend" values={{ vendor: vendorGrowth[vendorGrowth.length - 1].vendor, v: vendorGrowth[vendorGrowth.length - 1].growth!.toFixed(1) }} components={{ b: <strong /> }} /></span>
            </div>
          )}
        </div>
      </Card>

      {/* Stacked area chart */}
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-4">{t('subscriptions.ai_tools.chart_evolution')}</h3>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={stackedData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v) => fmt(Number(v))} />
            <Legend />
            {[...top8Vendors, ...(stackedData.some((d) => d['Others']) ? ['Others'] : [])].map((vendor, i) => (
              <Area
                key={vendor}
                type="monotone"
                dataKey={vendor}
                stackId="1"
                fill={COLORS[i % COLORS.length]}
                stroke={COLORS[i % COLORS.length]}
                fillOpacity={0.7}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* AI Share over time */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-4">{t('subscriptions.ai_tools.chart_share')}</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={aiShareData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v, name) => [name === 'AI Share %' ? `${v}%` : v, name]} />
              <Bar dataKey="AI Share %" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Adoption breadth table */}
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-3">{t('subscriptions.ai_tools.chart_ranking')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground text-xs uppercase tracking-wide">
                  <th className="text-left py-1.5 pr-3 font-medium">{t('subscriptions.ai_tools.col_vendor')}</th>
                  <th className="text-right py-1.5 pr-3 font-medium">{t('subscriptions.ai_tools.col_amount')}</th>
                  <th className="text-right py-1.5 pr-3 font-medium">{t('subscriptions.ai_tools.col_users')}</th>
                  <th className="text-right py-1.5 font-medium">{t('subscriptions.ai_tools.col_cards')}</th>
                </tr>
              </thead>
              <tbody>
                {vendorTotals.slice(0, 10).map((v) => (
                  <tr key={v.vendor} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-1.5 pr-3">
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{v.vendor}</span>
                        {newVendorsThisMonth.includes(v.vendor) && (
                          <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">{t('subscriptions.ai_tools.new_badge')}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-1.5 pr-3 text-right">{fmt(v.total)}</td>
                    <td className="py-1.5 pr-3 text-right">{v.users}</td>
                    <td className="py-1.5 text-right">{v.cards}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Optimization candidates */}
      {optimizationCandidates.length > 0 && (
        <Card className="p-5">
          <h3 className="text-sm font-semibold mb-1">{t('subscriptions.ai_tools.optimization_title')}</h3>
          <p className="text-xs text-muted-foreground mb-3">{t('subscriptions.ai_tools.optimization_desc')}</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground text-xs uppercase tracking-wide">
                  <th className="text-left py-2 pr-4 font-medium">{t('subscriptions.ai_tools.col_vendor')}</th>
                  <th className="text-right py-2 pr-4 font-medium">{t('subscriptions.ai_tools.col_amount')}</th>
                  <th className="text-right py-2 pr-4 font-medium">{t('subscriptions.ai_tools.col_users')}</th>
                  <th className="text-right py-2 pr-4 font-medium">{t('subscriptions.ai_tools.col_cards')}</th>
                  <th className="text-left py-2 font-medium">{t('subscriptions.ai_tools.optimization_title')}</th>
                </tr>
              </thead>
              <tbody>
                {optimizationCandidates.map((v) => (
                  <tr key={v.vendor} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="py-2 pr-4 font-medium">{v.vendor}</td>
                    <td className="py-2 pr-4 text-right font-semibold">{fmt(v.total)}</td>
                    <td className="py-2 pr-4 text-right">{v.users}</td>
                    <td className="py-2 pr-4 text-right">{v.cards}</td>
                    <td className="py-2 text-xs text-muted-foreground">{v.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
